import { useState, useMemo } from "react";
import { InsightsNavbar } from "@/components/InsightsNavbar";
import { InsightCard } from "@/components/InsightCard";
import { AnimatedSection } from "@/components/AnimatedSection";
import { Footer } from "@/components/Footer";
import { SEO, createFAQSchema } from "@/components/SEO";
import { getLatestInsights, insights } from "@/data/insights";
import { BookOpen, TrendingUp, Mail, Shield, Ban, FileText, Eye, Inbox, ArrowRight, Accessibility, Sparkles, Zap, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FeaturedInsightCarousel } from "@/components/FeaturedInsightCarousel";

const toolsPreview = [
  { id: "auth-checker", title: "SPF/DKIM/DMARC", icon: Shield, gradient: "from-emerald-500 to-teal-600", iconBg: "bg-gradient-to-br from-emerald-500/20 to-teal-600/20", badge: "Popular" },
  { id: "blacklist-checker", title: "Blacklist Scanner", icon: Ban, gradient: "from-red-500 to-rose-600", iconBg: "bg-gradient-to-br from-red-500/20 to-rose-600/20" },
  { id: "header-analyzer", title: "Header Decoder", icon: FileText, gradient: "from-amber-500 to-yellow-600", iconBg: "bg-gradient-to-br from-amber-500/20 to-yellow-600/20", badge: "AI" },
  { id: "tab-predictor", title: "Gmail Tab", icon: Inbox, gradient: "from-blue-500 to-indigo-600", iconBg: "bg-gradient-to-br from-blue-500/20 to-indigo-600/20", badge: "AI" },
  { id: "content-analyzer", title: "Accessibility", icon: Accessibility, gradient: "from-indigo-500 to-purple-600", iconBg: "bg-gradient-to-br from-indigo-500/20 to-purple-600/20", badge: "AI" },
  { id: "email-preview", title: "Email Preview", icon: Eye, gradient: "from-cyan-500 to-blue-600", iconBg: "bg-gradient-to-br from-cyan-500/20 to-blue-600/20" },
];

// Extract unique categories from insights
const getCategories = () => {
  const tags = new Set(insights.map(i => i.tag));
  return ["All", ...Array.from(tags).sort()];
};

const Home = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const allInsights = getLatestInsights();
  const latestInsights = useMemo(() => getLatestInsights(5), []); // Top 5 for carousel
  const categories = useMemo(() => getCategories(), []);

  const filteredInsights = useMemo(() => {
    let result = allInsights;
    
    // Filter by category
    if (activeCategory !== "All") {
      result = result.filter(i => i.tag === activeCategory);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(i => 
        i.title.toLowerCase().includes(query) ||
        i.subtitle.toLowerCase().includes(query) ||
        i.description.toLowerCase().includes(query) ||
        i.tag.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [allInsights, activeCategory, searchQuery]);

  const showFeatured = activeCategory === "All" && !searchQuery.trim() && latestInsights.length > 0;

  // FAQ schema for GEO
  const faqSchema = createFAQSchema([
    {
      question: "What is email deliverability?",
      answer: "Email deliverability is the ability to deliver emails to subscribers' inboxes. It measures whether your emails reach the inbox, spam folder, or get blocked entirely."
    },
    {
      question: "Why do December email reply rates drop?",
      answer: "December email reply rates drop due to holiday seasonality - people are on vacation, inboxes are flooded with promotional emails, and attention is focused on family and celebrations, not business communications."
    },
    {
      question: "What are SPF, DKIM, and DMARC?",
      answer: "SPF, DKIM, and DMARC are email authentication protocols. SPF specifies which servers can send email for your domain. DKIM adds a digital signature to verify email integrity. DMARC tells receivers what to do with emails that fail authentication."
    },
    {
      question: "How do I improve my email deliverability?",
      answer: "Improve email deliverability by authenticating your domain (SPF, DKIM, DMARC), warming up new domains gradually, maintaining list hygiene, avoiding spam trigger words, and sending relevant content that recipients engage with."
    }
  ]);

  return (
    <>
      <SEO
        title="IT Dukes Insights"
        description="Data-driven research on email deliverability, AI development, cybersecurity, and modern development practices. Free AI-powered tools and actionable insights backed by real-world data."
        canonical="/"
        keywords={["email deliverability", "SPF DKIM DMARC", "cold email", "AI coding", "vibe coding", "email authentication", "cybersecurity", "phishing detection"]}
        jsonLd={faqSchema}
      />
      <InsightsNavbar />
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/30" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-8 sm:pb-10">
            <AnimatedSection delay={0} className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-5">
                <BookOpen className="w-4 h-4" />
                Email Deliverability & Tech Research
              </div>
              
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-3">
                IT Dukes Insights
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                Data-driven research on email deliverability, AI development, cybersecurity, 
                and modern development practices. Actionable insights backed by real-world data.
              </p>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap items-center justify-center gap-5 mt-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Mail className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span><strong className="text-foreground">10B+</strong> emails analyzed</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-7 h-7 rounded-lg bg-success/10 flex items-center justify-center">
                    <TrendingUp className="w-3.5 h-3.5 text-success" />
                  </div>
                  <span><strong className="text-foreground">Industry</strong> benchmarks</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Content Section */}
        <section className="relative pb-12 sm:pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {/* Tools Quick Links - Premium Design */}
            <AnimatedSection delay={50} className="mb-8 sm:mb-10">
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card via-card to-muted/30 border border-border p-5">
                {/* Background Effects */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
                
                <div className="relative">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center border border-primary/10">
                        <Sparkles className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                          AI-Powered Tools
                        </h2>
                        <p className="text-xs text-muted-foreground">Free deliverability toolkit</p>
                      </div>
                    </div>
                    <Link 
                      to="/tools"
                      className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 hover:bg-primary text-primary hover:text-primary-foreground text-sm font-medium transition-all"
                    >
                      <span>View All 23</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                  
                  {/* Tools Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {toolsPreview.map((tool) => (
                      <Link
                        key={tool.id}
                        to="/tools"
                        onClick={() => {
                          sessionStorage.setItem('openTool', tool.id);
                        }}
                        className="group relative flex flex-col items-center gap-3 p-4 rounded-xl bg-card/80 backdrop-blur-sm border border-border hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 transition-all duration-300"
                      >
                        {tool.badge && (
                          <div className="absolute -top-1.5 -right-1.5">
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                              tool.badge === "AI" 
                                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30" 
                                : "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                            }`}>
                              {tool.badge === "AI" && <Zap className="w-2 h-2" />}
                              {tool.badge}
                            </span>
                          </div>
                        )}

                        <div className={`w-12 h-12 rounded-xl ${tool.iconBg} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <tool.icon className="w-6 h-6 text-primary" />
                        </div>

                        <span className="text-xs font-medium text-center text-muted-foreground group-hover:text-foreground transition-colors line-clamp-1">
                          {tool.title}
                        </span>

                        <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${tool.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity pointer-events-none`} />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Featured Insight Banner */}
            {showFeatured && (
              <AnimatedSection delay={100} className="mb-10 sm:mb-12">
                <FeaturedInsightCarousel insights={latestInsights} autoPlayInterval={6000} />
              </AnimatedSection>
            )}

            {/* Category Filters + All Insights Grid */}
            <AnimatedSection delay={200}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-foreground">
                  All Insights
                </h2>
                <span className="text-sm text-muted-foreground hidden sm:block">
                  {filteredInsights.filter(i => !i.comingSoon).length} published
                </span>
              </div>

              {/* Search Bar */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search insights by title, topic, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 bg-card border-border"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {categories.map((category) => {
                  const count = category === "All" 
                    ? allInsights.length 
                    : allInsights.filter(i => i.tag === category).length;
                  const isActive = activeCategory === category;
                  
                  return (
                    <Button
                      key={category}
                      variant={isActive ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveCategory(category)}
                      className={`rounded-full text-xs transition-all ${
                        isActive 
                          ? "bg-primary text-primary-foreground shadow-md" 
                          : "bg-card hover:bg-muted border-border"
                      }`}
                    >
                      {category}
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                        isActive ? "bg-primary-foreground/20" : "bg-muted-foreground/10"
                      }`}>
                        {count}
                      </span>
                    </Button>
                  );
                })}
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6">
                {filteredInsights.map((insight) => (
                  <InsightCard key={insight.slug} insight={insight} />
                ))}
              </div>

              {filteredInsights.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  No insights found in this category yet.
                </div>
              )}
            </AnimatedSection>

            {/* Newsletter CTA */}
            <AnimatedSection delay={300} className="mt-12 sm:mt-16">
              <div className="section-card bg-gradient-to-br from-primary/5 to-primary/10 text-center border-primary/20">
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  Stay Updated on Email Deliverability
                </h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                  We're continuously researching email deliverability patterns and best practices. 
                  New insights are published regularly with actionable data.
                </p>
                <a 
                  href="https://itdukes.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Learn more at ITDukes.com
                </a>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Home;